import { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";

import { Card, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";
import { useForm, Controller, set } from "react-hook-form";

import { AccountContext } from "context/account";
import MyProjectsContext from "context/my-projects";

import ProjectCard2 from "components/UpStyled/project-card/ProjectCard2";

import api from "services/api";
import { projectSortOptions, projectStatusOptions } from "utils/data";
import SearchIcon from "@mui/icons-material/Search";
import Prompt from "components/UpStyled/Prompt";

const explanation = [
  {
    title: "Requests",
    stage: "requested",
    text: "Projects have been submitted by designers, and are waiting for your responses.",
  },
  {
    title: "Prospects",
    stage: "prospects",
    text: "List of projects that you have either saved or replied to.",
  },
  {
    title: "Ongoing Projects",
    stage: "active",
    text: "Projects in action once designers have selected you as the final manufacturer to work on their projects.",
  },
  {
    title: "Completed Projects",
    stage: "completed",
    text: "Projects and services have been completed according to the mutual agreement with designers.",
  },
  {
    title: "Canceled Projects",
    stage: "canceled",
    text: "Ongoing Projects can be canceled ONLY IF both parties have agreed.",
  },
  {
    title: "All Projects",
    stage: "all",
    text: "",
  },
];

const ProjectHome = ({ stage = "" }) => {
  const [projectList, setProjectList] = useState([]);
  const [filteredProjectList, setFilteredProjectList] = useState([]);
  const [totalNumber, setTotalNumber] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [orderOpt, setOrderOpt] = useState(projectSortOptions[0]);
  const [searchStr, setSearchStr] = useState("");
  const [chats, setChats] = useState(new Map());
  const [selectedStatus, setSelectedStatus] = useState(["active"]);
  const account = useContext(AccountContext);
  const {
    stagesProjects,
    prospects,
    removeMyFavoriteProject,
    addUnFavoriteProject,
    handleRefresh,
  } = useContext(MyProjectsContext);

  const url = location.href ? location.href : location.pathname + location.search;

  const hint = explanation.find((x) => x.stage == stage);

  useEffect(() => {
    handleRefresh();
  }, []);

  useEffect(() => {
    if (account.id) {
      retrieveProjects(stage);
      retrieveChats();
    }
  }, [account, url, prospects, stagesProjects]);

  const retrieveProjects = async (stage) => {
    let projects = [];
    if (stage == "prospects") {
      projects = prospects;
    } else {
      projects = stagesProjects[stage] || [];
    }
    const totalNumber = projects.length;
    setTotalNumber(totalNumber);
    const sortedProjects = projects.sort((a, b) => compare(a, b, orderOpt?.value));

    setProjectList(sortedProjects);
    if (stage == "prospects") {
      handleFilterProjectStatus(1, pageSize, searchStr, [], sortedProjects);
    } else {
      handleFilterProjectStatus(1, pageSize, searchStr, ["active"], sortedProjects);
    }
  };

  const retrieveChats = async () => {
    try {
      api.chatroom.listChatRoom("project", "", "").then(({ data }) => {
        var chatrooms = new Map();
        for (const room of data.chats) {
          if (chatrooms.has(room.regardingid) && chatrooms.get(room.regardingid).ts > room.ts) {
            continue;
          }
          chatrooms.set(room.regardingid, room);
        }
        setChats(chatrooms);
        // console.log("chatrooms", chatrooms);
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleFilterProjectStatus = (
    page,
    pageSize,
    str = searchStr,
    mySelectedStatus = null,
    myProjectList = null
  ) => {
    let copyProjects = myProjectList ? [...myProjectList] : [...projectList];

    if (mySelectedStatus) {
      if (mySelectedStatus.length > 0) {
        copyProjects = copyProjects.filter((x) => mySelectedStatus.includes(x.status));
      }
      setSelectedStatus(mySelectedStatus);
    } else {
      if (selectedStatus.length > 0) {
        copyProjects = copyProjects.filter((x) => selectedStatus.includes(x.status));
      }
    }

    let filteredList = copyProjects.map((x) => {
      let StatusOpt = projectStatusOptions.find((opt) => x.status == opt.value);
      return { ...x, statusLabel: StatusOpt?.label ? StatusOpt.label : x.status };
    }); //all projects

    if (str) {
      const strUpper = str.toUpperCase();
      const searchFields = ["name", "description", "statusLabel"];
      filteredList = filteredList.filter((x) => {
        let result = false;
        for (let i = 0; i < searchFields.length; i++) {
          if (x[searchFields[i]]) {
            if (typeof x[searchFields[i]] == "string") {
              if (x[searchFields[i]].toUpperCase().includes(strUpper)) {
                result = true;
              }
            } else if (typeof x[searchFields[i]] == "object") {
              const text = JSON.stringify(x[searchFields[i]]);
              if (text.toUpperCase().includes(strUpper)) {
                result = true;
              }
            }
          }
        }
        return result;
      });
    }
    const totalNumber = filteredList.length;
    setTotalNumber(totalNumber);

    //filter based on pagination
    const sliceStart = pageSize * (page - 1);
    const sliceEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;
    const newFilteredList = filteredList.slice(sliceStart, sliceEnd);
    setFilteredProjectList(newFilteredList);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    handleFilterProjectStatus(newPage, pageSize, searchStr);
  };

  const handleChangeRowsPerPage = (option) => {
    const newPageSize = parseInt(option.value, 10);
    setPageSize(newPageSize);
    setPage(1);
    handleFilterProjectStatus(1, newPageSize, searchStr);
  };

  const pageCount = parseInt(Math.ceil(totalNumber / pageSize), 10);
  const pageOptions = [];
  for (let i = 0; i < pageCount; i++) {
    pageOptions.push(i);
  }
  const entriesStart = pageSize * (page - 1) + 1;
  const entriesEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;

  const handleSearchChange = (str) => {
    setSearchStr(str);
    handleFilterProjectStatus(page, pageSize, str);
  };

  const handleOnSortChange = (e) => {
    setOrderOpt(e);
    let sortStr = e?.value;
    const newFilteredProjectList = filteredProjectList.sort((a, b) => compare(a, b, sortStr));
    setFilteredProjectList(newFilteredProjectList);
  };

  const compare = (a, b, key) => {
    let isAscOrder = true;
    if (key.startsWith("-")) {
      key = key.slice(1);
      isAscOrder = false;
    }
    if (a[key].toLowerCase() < b[key].toLowerCase()) {
      return isAscOrder ? -1 : 1;
    }
    if (a[key].toLowerCase() > b[key].toLowerCase()) {
      return isAscOrder ? 1 : -1;
    }
    return 0;
  };

  const removeProspect = async (project) => {
    try {
      await addUnFavoriteProject(account.id, account.account_type, project.id);
      /*
      const newProjectList = projectList.filter((x) => x.id != project.id);
      const sliceStart = 0;
      const sliceEnd = pageSize;
      const newFilteredList = newProjectList.slice(sliceStart, sliceEnd);

      setFilteredProjectList(newFilteredList);

      setProjectList(newProjectList);
      setTotalNumber(newProjectList.length);
      setPage(1);
*/
      toast.success(`Removed from my prospect successfully!`);
    } catch (err) {
      toast.error(`Removed from my prospect failed!`);
    }
  };

  const handleChangeProjectStatus = (e) => {
    const selectedValue = e?.target?.value || "";
    if (selectedStatus.includes(selectedValue)) {
      let newSelectedStatus = selectedStatus.filter((x) => x != selectedValue);
      handleFilterProjectStatus(1, pageSize, searchStr, newSelectedStatus);
    } else {
      let newSelectedStatus = [...selectedStatus, selectedValue];
      handleFilterProjectStatus(1, pageSize, searchStr, newSelectedStatus);
    }
    setPage(1);
  };
  const getTooltipForStatus = (status) => {
    let record = explanation.find((exp) => exp.stage == status.value);
    return record?.text ? record.text : status.label;
  };

  return (
    <div sx={{ p: 3 }}>
      <SuiBox pb={3}>
        <SuiBox>
          <SuiBox display="flex" justifyContent="space-between">
            <SuiTypography variant="h5" textTransform="capitalize">
              {hint?.title ? hint.title : "Project Board"}
            </SuiTypography>
          </SuiBox>
          {hint?.text ? (
            <SuiBox mb={1.5} ml={0.5} mt={0.5} lineHeight={0} display="inline-block">
              <SuiTypography component="label" variant="caption" fontWeight="regular" color="text">
                {hint.text}
              </SuiTypography>
            </SuiBox>
          ) : null}
        </SuiBox>

        <SuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          {stage != "prospects" ? (
            <SuiBox>
              <ButtonGroup
                variant="outlined"
                aria-label="text button group"
                color="dark"
                onClick={handleChangeProjectStatus}
              >
                {projectStatusOptions
                  .filter((x) => x.value != "init" && x.value != "requested")
                  .map((x) => (
                    <Tooltip title={getTooltipForStatus(x)}>
                      <SuiButton
                        variant={selectedStatus.includes(x.value) ? "contained" : "outlined"}
                        value={x.value}
                        color={selectedStatus.includes(x.value) ? "info" : "dark"}
                        size="small"
                        sx={{ px: 1, fontSize: 11 }}
                      >
                        {x.label}({stagesProjects[x.value] ? stagesProjects[x.value].length : 0})
                      </SuiButton>
                    </Tooltip>
                  ))}
              </ButtonGroup>
            </SuiBox>
          ) : (
            <SuiBox />
          )}
          <SuiBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
            {" "}
            <SuiBox display="flex" alignItems="center" gap={2}>
              <Card sx={{ borderRadius: 2, maxWidth: 145 }}>
                <SuiSelect
                  options={projectSortOptions}
                  placeholder="Sort"
                  onChange={handleOnSortChange}
                  value={orderOpt}
                />
              </Card>
            </SuiBox>
            <SuiBox>
              <SuiInput
                placeholder="Search Keywords"
                type="text"
                onChange={(e) => handleSearchChange(e?.target?.value)}
                icon={{
                  direction: "right",
                  component: (
                    <SearchIcon
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleSearchChange(searchStr)}
                    />
                  ),
                }}
              />
            </SuiBox>
          </SuiBox>
        </SuiBox>
        <div>
          {filteredProjectList && filteredProjectList.length > 0 ? (
            filteredProjectList.map((x, index) => (
              <ProjectCard2
                key={index}
                project={x}
                chats={chats}
                removeProspect={stage == "prospects" ? () => removeProspect(x) : null}
                detailUrl={`/job/requests?id=${x.id}`}
                actions={
                  <SuiBox
                    display="flex"
                    justifyContent="space-between"
                    alignitems="center"
                    sx={{ gap: 1 }}
                  ></SuiBox>
                }
              />
            ))
          ) : (
            <SuiBox px={2} py={15}>
              <SuiTypography variant="button" color="secondary" fontWeight="regular">
                No available projects!
              </SuiTypography>
            </SuiBox>
          )}
        </div>

        <SuiBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={3}
        >
          <div className="flex items-center">
            <SuiBox mb={{ xs: 3, sm: 0 }}>
              <SuiTypography variant="button" color="secondary" fontWeight="regular">
                Showing {entriesStart} to {entriesEnd} of {totalNumber} entries
              </SuiTypography>
            </SuiBox>

            <SuiBox display="flex" alignItems="center" gap={1}>
              <SuiSelect
                defaultValue={{ value: pageSize, label: pageSize }}
                options={[10, 20, 50].map((entry) => ({ value: entry, label: entry }))}
                onChange={handleChangeRowsPerPage}
                size="small"
              />

              <SuiTypography variant="caption" color="secondary">
                entries per page
              </SuiTypography>
            </SuiBox>
          </div>
          <Pagination
            variant={"outlined"}
            color={"info"}
            count={pageOptions.length}
            defaultPage={page}
            onChange={handleChangePage}
          />
        </SuiBox>
      </SuiBox>
    </div>
  );
};

export default ProjectHome;
